import { useState } from 'react';
import { cn } from '@scio3/ui';
import type { AppMeta } from '@scio3/shared-types';
import { APP_PREVIEW_IMAGES } from '@/lib/brand-assets';

/**
 * A real product screenshot on the pre-hook's TV, for the apps we have one for.
 *
 * Renders OVER `PrehookDashboard` (the animated placeholder) instead of replacing it, which
 * makes the animation the loading state and the fallback in one move: no entry in
 * `APP_PREVIEW_IMAGES`, a slow fetch, or a failed one all leave the visitor watching the
 * animation rather than an empty screen. The photo fades in when it decodes (`prehook.css`).
 *
 * Decorative, hence `alt=""` + `aria-hidden`: the headline beside it already names the app,
 * and the screenshot carries no information a screen-reader user is missing.
 *
 * Still generic — the map is keyed by `appType` and this file names no app.
 */

interface PrehookPreviewProps {
  appMeta: AppMeta;
}

export function PrehookPreview({ appMeta }: PrehookPreviewProps) {
  const src = appMeta.appType ? APP_PREVIEW_IMAGES[appMeta.appType] : undefined;
  const [status, setStatus] = useState<'loading' | 'loaded' | 'failed'>('loading');

  // Nothing to show, or the CDN said no: the placeholder underneath is already the answer.
  if (!src || status === 'failed') return null;

  return (
    <img
      src={src}
      alt=""
      aria-hidden
      data-testid="app-intent-prehook-preview"
      className={cn(
        'prehook-preview absolute inset-0 h-full w-full object-cover',
        status === 'loaded' && 'prehook-preview-in',
      )}
      onLoad={() => setStatus('loaded')}
      onError={() => setStatus('failed')}
      decoding="async"
      // Same reasoning as the logo tile in `PrehookAppIcon`, and never `loading="lazy"`.
      fetchPriority="high"
    />
  );
}
